import { Component, EventEmitter, Injector, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { AbstractComponent, ChatContextRef } from 'lib-trend-core';
import { CHAT_CONTEXT_REF_NAVIGATOR, ChatContextRefNavigator } from '../chat.tokens';
import { contextRefIcon, contextRefKindLabelKey, contextRefRoute } from './chat-context-ref.util';

/**
 * Work-reference card (SPEC-2026-07-internal-chat, T-22 / RF-35 / AC-31) — the
 * Trendmulti differentiator: a chat message can point at an Attendance, a CRM
 * Card or a Contact, and the reference renders inline as a clickable card.
 *
 * Renders ONLY the snapshot persisted with the message (kind + label +
 * subtitle) — it never fetches the referenced record, so no data the sender
 * could not see is ever revealed here. Clicking deep-links into the target
 * module, whose own route guards and permissions decide what the reader may
 * actually open.
 *
 * Where the record opens is the host's decision (SPEC-2026-09-internal-chat-pwa,
 * RF-25): the product routes in-app; the chat app provides
 * `CHAT_CONTEXT_REF_NAVIGATOR` and opens the product in a named tab.
 */
@Component({
  selector: 'chat-context-ref-card',
  templateUrl: './chat-context-ref-card.component.html',
  styleUrls: ['./chat-context-ref-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatContextRefCardComponent extends AbstractComponent {

  @Input() reference: ChatContextRef | null = null;

  /** Compact variant used inside the composer tray (shows the remove button). */
  @Input() removable = false;

  /** Emitted by the removable variant — the composer drops the pending reference. */
  @Output() remove = new EventEmitter<void>();

  private readonly navigator: ChatContextRefNavigator | null;

  constructor(injector: Injector) {
    super(injector);
    this.navigator = injector.get(CHAT_CONTEXT_REF_NAVIGATOR, null);
  }

  get icon(): string {
    return this.reference ? contextRefIcon(this.reference.kind) : 'ri-links-line';
  }

  get kindLabelKey(): string {
    return this.reference ? contextRefKindLabelKey(this.reference.kind) : 'chat.contextRef.kindUnknown';
  }

  get label(): string {
    return this.reference?.snapshot?.label ?? '';
  }

  get subtitle(): string {
    return this.reference?.snapshot?.subtitle ?? '';
  }

  get navigable(): boolean {
    return !!contextRefRoute(this.reference);
  }

  /** Deep link into the target module (attendance panel / kanban card / contact). */
  open(): void {
    const target = contextRefRoute(this.reference);
    if (!target) return;
    if (this.navigator) {
      this.navigator.open(this.reference);
      return;
    }
    this.router.navigate(target.commands, target.queryParams ? { queryParams: target.queryParams } : undefined);
  }
}
